/**
 * src/data/events.js
 *
 * Layer 1 product analytics event log + sync seam (Phase 32 KPI-01).
 * This is the single source of truth for client-side analytics in Tend.
 *
 * ZERO EXTERNAL NETWORK CALLS in v2.3 — all operations are pure localStorage.
 * `flushEvents()` is a no-op stub that returns Promise.resolve(); v3.0 (Phase
 * 999.3) replaces the body with the Supabase POST. No fetch(), XMLHttpRequest,
 * sendBeacon, or axios in this module yet — see docs/COST_ARCHITECTURE.md §3.
 *
 * Shape (per D-02 + PRODUCT-ANALYTICS.md §2 example):
 *   { e: '<event-name>', ts: '<ISO-8601 string>', ...props }
 * Properties live as siblings of `e`/`ts`. NO nested `properties` object.
 *
 * Export order:
 *   1) logEvent(name, props?)     — append to tend:events, FIFO cap 1,000 (D-01)
 *   2) getEvents()                — return parsed array (newest last)
 *   3) clearEvents()              — wipe tend:events (dev-mode "Clear analytics", D-06)
 *   4) flushEvents()              — Promise<void>, no-op in v2.3 (D-05)
 *
 * Sync model (D-04): tend:eventsSyncCursor (separate key, epoch-ms) holds the
 * last-synced timestamp; v3.0 reads cursor → POSTs events with ts > cursor →
 * advances cursor on 200. Events themselves stay clean (no per-event flag).
 */

const STORAGE_KEY = 'tend:events';
const FIFO_CAP    = 1000;

/**
 * Append an event to tend:events (FIFO cap 1,000).
 *
 * Never throws. Never blocks navigation. Silent in prod; console.warn in dev.
 * Wrapped in try/catch to handle localStorage QuotaExceeded and private mode.
 *
 * @param {string} name - event name (e.g. 'app_open', 'task_completed')
 * @param {object} [props] - extra properties as siblings of {e, ts}
 * @returns {void}
 */
export function logEvent(name, props = {}) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(arr) ? arr : [];
    list.push({
      e:  name,
      ts: new Date().toISOString(),
      ...props,
    });
    // FIFO cap: enforce on write so storage cost is bounded regardless of
    // tampering. slice(-1000) retains newest 1,000 entries (D-01).
    const capped = list.length > FIFO_CAP ? list.slice(-FIFO_CAP) : list;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(capped));
  } catch (err) {
    // eslint-disable-next-line no-undef
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[tend] events log write failed:', err);
    }
    // Silent in prod — caller proceeds regardless.
  }
}

/**
 * Read the current tend:events array. Defensive — returns [] on any failure.
 * @returns {Array<{e: string, ts: string, [k: string]: any}>}
 */
export function getEvents() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/**
 * Wipe tend:events. Used by Settings → Developer "Clear analytics" (D-06).
 * Never throws.
 */
export function clearEvents() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {} // eslint-disable-line no-empty
}

/**
 * Drain pending events to Supabase. v2.3 ships a no-op; v3.0 (Phase 999.3)
 * fills the body with: read tend:eventsSyncCursor → POST events with ts > cursor →
 * advance cursor on 200. Mirrors the affiliateClicks "sync deferred" pattern.
 *
 * Wired now from App.jsx visibilitychange listener so the v3.0 cutover is
 * body-only — zero call-site changes during a high-stakes cloud cutover.
 *
 * @returns {Promise<void>}
 */
export function flushEvents() {
  return Promise.resolve();
}
